import { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { LanguageProvider } from './contexts/LanguageContext';
import { DashboardSkeleton } from './components/LoadingSkeleton';
import PWAInstallPrompt from './components/PWAInstallPrompt';
import OfflineIndicator from './components/OfflineIndicator';
import PWAUpdateNotifier from './components/PWAUpdateNotifier';
import '@/App.css';

// Immediate Load Pages (Landing, Auth)
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Lazy Load Pages (for code splitting)
const DriverDashboard = lazy(() => import('./pages/DriverDashboard'));
const HelperDashboard = lazy(() => import('./pages/HelperDashboard'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const PaymentSuccess = lazy(() => import('./pages/PaymentSuccess'));
const PaymentCancel = lazy(() => import('./pages/PaymentCancel'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));
const ProfileSettings = lazy(() => import('./pages/ProfileSettings'));
const HelperEarnings = lazy(() => import('./pages/HelperEarnings'));
const HelperReviews = lazy(() => import('./pages/HelperReviews'));
const DriverReviews = lazy(() => import('./pages/DriverReviews'));
const CreateEstimate = lazy(() => import('./pages/CreateEstimate'));
const ViewEstimate = lazy(() => import('./pages/ViewEstimate'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Axios interceptor for auth
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAuth();
    
    // Safety timeout: longer for mobile devices due to slower connections
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const timeoutDuration = isMobile ? 30000 : 15000; // 30s for mobile, 15s for desktop
    
    const safetyTimeout = setTimeout(() => {
      setLoading(false);
      console.error('Loading timeout - forcing stop');
    }, timeoutDuration);
    
    return () => clearTimeout(safetyTimeout);
  }, []);

  const checkAuth = async () => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        // Longer timeout for mobile devices
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const requestTimeout = isMobile ? 20000 : 10000; // 20s for mobile, 10s for desktop
        
        const response = await axios.get(`${API}/auth/me`, {
          timeout: requestTimeout
        });
        setUser(response.data);
      } catch (error) {
        console.error('Auth check failed:', error);
        localStorage.removeItem('token');
        // Silently handle auth errors without showing toasts
        // User will simply see landing page if not authenticated
      }
    }
    setLoading(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setUser(null);
    toast.success('Logged out successfully');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="App">
      <Toaster position="top-right" richColors />
      <PWAInstallPrompt />
      <OfflineIndicator />
      <PWAUpdateNotifier />
      <LanguageProvider>
        <BrowserRouter>
          <Suspense fallback={<div className="min-h-screen bg-gray-50"><DashboardSkeleton /></div>}>
            <Routes>
              <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
              <Route path="/auth" element={user ? <Navigate to="/dashboard" /> : <AuthPage setUser={setUser} />} />
              <Route path="/forgot-password" element={user ? <Navigate to="/dashboard" /> : <ForgotPassword />} />
              <Route path="/reset-password" element={user ? <Navigate to="/dashboard" /> : <ResetPassword />} />
            <Route
              path="/dashboard"
              element={
                user ? (
                  user.role === 'admin' ? (
                    <AdminDashboard user={user} onLogout={handleLogout} />
                  ) : user.role === 'driver' ? (
                    <DriverDashboard user={user} onLogout={handleLogout} />
                  ) : (
                    <HelperDashboard user={user} onLogout={handleLogout} />
                  )
                ) : (
                  <Navigate to="/auth" />
                )
              }
            />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-cancel" element={<PaymentCancel />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route
              path="/profile-settings"
              element={user ? <ProfileSettings /> : <Navigate to="/auth" />}
            />
            <Route
              path="/helper-earnings"
              element={user ? <HelperEarnings /> : <Navigate to="/auth" />}
            />
            <Route
              path="/helper-reviews"
              element={user ? <HelperReviews user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />}
            />
            <Route
              path="/driver-reviews"
              element={user ? <DriverReviews user={user} onLogout={handleLogout} /> : <Navigate to="/auth" />}
            />
            <Route
              path="/create-estimate/:requestId"
              element={user ? <CreateEstimate /> : <Navigate to="/auth" />}
            />
            <Route
              path="/estimate/:estimateId"
              element={user ? <ViewEstimate user={user} /> : <Navigate to="/auth" />}
            />
            <Route
              path="/estimate/request/:requestId"
              element={user ? <ViewEstimate user={user} /> : <Navigate to="/auth" />}
            />
          </Routes>
          </Suspense>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
